/**
 * 案件の画面（033 → 039、docs/28 §4）。/groups/:id
 *   ・パンくず：案件 ＞ {案件名}。見出しに状態のチップ（見積から自動で決まる。手で付けるのは「完了」だけ）
 *   ・上から：数字の枠4つ（受注・請求済・利益・未受注。ホームの「今月の数字」と同じ部品・言い方、利益はルール7。案件では低い枠）
 *     → 案件詳細（顧客・支店・顧客担当者・担当者・支払条件・現場住所・施主。「直す」でその場の欄に。説明は「？」）
 *     → 社内メモ（独立した枠。打つと自動で保存。「日時と名前を追記」で先頭に [日時 名前] を入れて続きを書く＝新しいメモが上）
 *     → 「見積」「請求」「外注」のタブ（2026-09-22 射場さんの指示。紙の宛名の見本は置かない。説明は「？」）
 *   ・2026-09-22 射場さん：「案件の内容」→「案件内容」・枠を低く・説明文は「？」に・社内メモは独立して自動保存
 *   ・2026-09-27 射場さん（#89）：見出しの状態のチップは案件名と上下の中央・同じ高さ、「完了」と「＋ 見積を追加」も中央でそろえる。
 *     「案件内容」→「案件詳細」、タブ「請求書」→「請求」・「外注見積」→「外注」・「読み取り済み」→ 緑の ✓「読取済」。請求が無いときの文は「請求書はありません。」＋「？」
 *   ・見積のタブ：受注してまだ請求書が無い行に「請求書作成」、ある行は請求書の番号へのリンク
 *   ・請求書のタブ：この案件の請求書すべて（番号・どの見積から・状態・金額・請求日・お支払期限）
 *   ・「見積追加」：すぐ下書きへ（支店・顧客担当者・担当者・工事場所を案件から写す。工事名は1枚目＝案件名、2枚目から「案件名（追加工事）」＝API が決める）
 *   ・すべて請求済みなら見出しのチップ「請求済み」と「完了」（#168 で帯をやめた）。見積が0枚なら「この案件を削除」
 *   ・#265（docs/81 §12）：見積が0枚でゴミ箱に見積書があれば「ゴミ箱に見積書 n件 ›」。番号なしだけなら削除は確認の窓（ゴミ箱の見積書も完全に削除・
 *     一覧の通知に「元に戻す」なし）、番号ありがあれば「{project}削除」は押せず下に1行
 *   ・9/29（#92、docs/35）：説明の文は「？」へ。1画面の「？」は 数字の枠（未入金の説明も利益の「？」に）・案件詳細（完了・見積追加・欄の説明・
 *     直しているときの請求タイミングの説明も）・社内メモ の3つ。担当者の欄の名前は「自社の担当者」（決め10）
 */
import {
  CONFLICT_MSG, GROUP_MEMO_APPEND_MAX, MEMO_MSG, rebaseFields, rebaseMemo,
  TAX_MSG, TRASH_MSG, RECURRING_MSG, BILLING_TIMING_LABEL, BILLING_MODE_LABEL, CLOSING_MSG, type BillingMode, GROUP_MSG, GROUP_STATUS_LABEL, defaultInstallments, installmentDefaultNames, paymentTermsLabel, type BillingPlan, type BillingTiming, type Estimate, type EstimateSettings, type GroupCreate, type GroupDetail, type GroupJob, type GroupUpdate, type Partner, type PaymentTerms } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, Suspense, lazy, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { api, ApiError, messageOf } from "../api.js";
import { AmtCell } from "../components/AmtCell.jsx";
import { BillingPlanEditor, PlanProgress } from "../components/BillingPlan.jsx";
import { BackButton, differs, useBackNav, usePageName } from "../components/BackButton.jsx";
import { BranchSelect, ContactSelect, TermsChooser, partyFieldsHelp } from "../components/GroupFields.jsx";
import { useTabIndicator } from "../components/ListTabs.jsx";
import { RowMenu } from "../components/RowMenu.jsx";
import { Icon } from "../components/Shell.jsx";
import { useMe } from "../auth.js";
import { ErrorText, Help, Switch, useToast } from "../components/ui.jsx";
import { DateInput } from "../components/DateInput.jsx";
import { useOngoing } from "../lib/ongoing.js";
import { t, useLabels } from "../labels.js";
import { md, yen } from "../lib/num.js";
import { StatusChip, useFlash } from "./Groups.jsx";
import { useCreateInvoice } from "./Invoices.jsx";
import { ESTIMATE_SETTINGS_KEY } from "./SettingsEstimate.jsx";
import { GroupRfqs } from "./rfq/GroupRfqs.jsx";
import { GroupOrders } from "./orders/OrderListPage.jsx";
import { GroupPurchases } from "./purchases/PurchaseListPage.jsx";
import { GroupNotices } from "./payment-notices/PaymentNoticeListPage.jsx";
import { DeliveryTable, useDeliveries } from "../components/Deliveries.jsx";
import { ACCEPTANCE_MSG, COVER_LETTER_MSG, DELIVERY_NOTE_MSG, ONGOING_MSG, PURCHASE_MSG, RFQ_MSG, monthLabelOf, type DeliveryNoteList, type GroupMonthly, type OrderList, type PurchaseList, type RfqList, type VendorQuoteList } from "@genbaryoku/contract";
import { GroupDeliveryNotes } from "./delivery-notes/DeliveryNoteListPage.jsx";
import { Slot, hasModule, useSlotPreload } from "../core/slots.jsx";
import { uses } from "../core/uses.js";
import { ScheduleTab } from "./schedule/ScheduleTab.jsx";
import { RecurringTab, useGroupRecurring } from "./recurring/GroupRecurringTab.jsx";
import { SiteGeoFollowDialog } from "../components/SiteGeoFollowDialog.jsx";
import { SiteMapFolded, SiteMapLoading, useSiteMapFold } from "../components/SiteMapLoading.jsx";
import { useNarrow } from "../lib/useNarrow.js";
import { usePcOnly } from "../lib/pcOnly.js";
import { useSaveQueue } from "../lib/saveQueue.jsx";
import { ymdShort } from "../lib/dates.js";
/** 現場の地図（#132）。MapLibre が大きいので、案件の画面を開いてから別に読む */
const SiteMap = lazy(() => import("../components/SiteMap.jsx").then((m) => ({ default: m.SiteMap })));
/** Google の地図（#137、docs/49 §13）。Me.features.map.provider が google のとき。Embed の iframe なので軽いが、地理院と同じく別に読む */
const GoogleSiteMap = lazy(() => import("../components/GoogleSiteMap.jsx").then((m) => ({ default: m.GoogleSiteMap })));

const INVOICE_STATUS: Record<string, string> = { draft: "下書き", issued: "出した", sent: "出した", paid: "入金済み" };

export function GroupPage() {
  const { id = "" } = useParams();
  const L = useLabels();
  const q = useQuery({ queryKey: ["group", id], queryFn: () => api<GroupDetail>("GET", `/groups/${id}`), retry: false });
  // 差し込み（受け取った書類）の中身も同時に取りに行き、そろってから描く（#179：後から入ると下の社内メモ・タブがずれて押し間違える）
  const slotsReady = useSlotPreload("group.received", useMe().data, { groupId: id });
  if (q.isPending || (q.isSuccess && !slotsReady)) return <p className="hint">読み込んでいます…</p>;
  if (q.isError) {
    return (
      <div className="soonbox">
        <ErrorText>{messageOf(q.error)}</ErrorText>
        <p>
          <Link className="btn text" to="/groups">{L.project}一覧へ</Link>
        </p>
      </div>
    );
  }
  return <GroupBody key={q.data.id} g={q.data} />;
}

type EditForm = { name: string; branchId: string | null; contactId: string | null; staff: string; terms: PaymentTerms | null; site: string; owner: string; timing: BillingTiming; billingMode: BillingMode | null; ongoing: boolean; startedOn: string };
/** {project}の欄（「修正」の形）。版の重ね直し（#202）でも使う */
const editFormOf = (g: GroupDetail): EditForm => ({
  name: g.name,
  branchId: g.branch?.id ?? null,
  contactId: g.contact?.id ?? null,
  staff: g.staffName,
  terms: g.paymentTerms.source === "group" ? { ...g.paymentTerms.terms } : null,
  site: g.siteAddress ?? "",
  owner: g.ownerName ?? "",
  timing: g.billingTiming,
  billingMode: g.billingMode ?? null,
  ongoing: g.isOngoing,
  startedOn: g.startedOn ?? "",
});

function GroupBody({ g }: { g: GroupDetail }) {
  const L = useLabels();
  /** 幅 767px 以下は見積・請求の表をやめて1件1枚のカード（S1。表の細い列で「出すときに付きます」が縦書きに崩れていた） */
  const narrow = useNarrow();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { toast, node: toastNode } = useToast();
  useFlash(toast);
  const createInvoice = useCreateInvoice(toast);
  const [edit, setEdit] = useState<EditForm | null>(null);
  /** マップの開閉（#235） */
  const mapFold = useSiteMapFold(g);
  /** 「直す」を開いたときの値（入れかけの確かめ。#98） */
  const editInit = useRef<EditForm | null>(null);
  // 戻る（#98、docs/37）：直前の画面があればそこ（{customer}の詳細・一覧のタブなど）。無ければ{project}の一覧。「直す」の入れかけは確かめる
  const back = useBackNav({ to: "/groups", label: L.project }, !!edit && differs(edit, editInit.current));
  usePageName(g.name, L.project);
  /** 分割の予定をその場で開いている見積（042）。見積の一覧・見積の画面から ?plan=見積の id で来たときは、最初から開く */
  const [sp0] = useSearchParams();
  const [planFor, setPlanFor] = useState<string | null>(() => {
    const q = sp0.get("plan");
    return q && g.jobs.some((j) => j.id === q) ? q : null;
  });
  const [saveErr, setSaveErr] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const pcOnly = usePcOnly();
  /** 送付状を作成（「⋮」とタブ「送付」の主ボタン。#223 の3回目の前の直し＝★14）：送付先は{project}の{customer}・支店、記録の{project}はこの{project} */
  const coverNewTo = `/cover-letters/new?partner=${g.client.id}${g.branch ? `&branch=${g.branch.id}` : ""}&group=${g.id}`;
  /** #132：住所を変えて保存するとき、手で直したピンがあれば「合わせますか」と聞く（答えが出るまで保存しない） */
  const [followAsk, setFollowAsk] = useState(false);
  const s = g.summary;
  const meQ = useMe();
  /** 継続案件（#289、docs/101 §7）：使う機能「{ongoingProject}」がオンの会社だけ札・開始日・月別の表・「継続」のスイッチ */
  const og = useOngoing();
  const asOngoing = og.on && g.isOngoing;
  /** 分割の既定の回は製品ごと（#142 段1）。会社の設定（設定 → 見積・請求）があればそちら（#142 段3） */
  const product = meQ.data?.tenant.product;
  const estSettings = useQuery({ queryKey: ESTIMATE_SETTINGS_KEY, queryFn: () => api<EstimateSettings>("GET", "/company/estimate-settings"), staleTime: 60_000 });
  const instDefaults = estSettings.data?.installments ?? null;
  /** 施主名はモジュール field の会社だけ（#142 段2、docs/52 §2） */
  const field = hasModule(meQ.data, "field");
  /** 請求の形（#267）は sales の会社だけ（締め請求書は門 sales） */
  const sales = !!meQ.data?.tenant.modules.includes("sales");
  /** 入金（E1。API が payments を返す会社＝sales だけ）：「入金」タブと数字の枠の「未入金」 */
  const pays = g.payments !== undefined && !!s.unpaid;
  // 数字の枠の「？」の終わりの一文（失注・未入金・税）。「利益」の枠（sales の会社は「粗利」の枠）の「？」に付ける
  const figuresNote = <>{s.lost.count ? `失注 ${s.lost.count}件は合計に入りません。` : ""}{pays && s.unpaid ? "未入金は、出した請求書の残（請求額 − 入金・手数料・値引き）の合計です。ホームの「入金待ち」と同じ数え方です。" : ""}{TAX_MSG.groupFiguresHelp}。</>;
  const fill = (text: string) => t(text).replace("{name}", g.name);
  // 直すときに使う 顧客の支店・担当者・支払条件。画面を開いたときに先に引いておく（#179：「修正」を押してから届くと、待つ間の欄と届いた欄で
  // 支店の段の数が違うことがあり（幅の狭い画面）、下の請求タイミング・保存がずれる。待つ間の形は下の group-edit-loading）
  const pq = useQuery({ queryKey: ["partner", g.client.id], queryFn: () => api<Partner>("GET", `/partners/${g.client.id}`) });
  const [partnerOverride, setPartnerOverride] = useState<Partner | null>(null);
  const partner = partnerOverride ?? pq.data ?? null;

  const refresh = (next?: GroupDetail) => {
    if (next) qc.setQueryData(["group", g.id], next);
    else void qc.invalidateQueries({ queryKey: ["group", g.id] });
    void qc.invalidateQueries({ queryKey: ["group-list"] });
    void qc.invalidateQueries({ queryKey: ["groups", g.client.id] });
  };

  /**
   * 「修正」の欄の元（#202）：どの版の{project}から作ったか。開いている間に{project}が変わったら（自分の社内メモの追記・画面に戻ったときの読み直し）、
   * この画面で修正中の欄だけを残して新しい{project}に重ねる（前の版のままの欄で、ほかの利用者の変更を上書きしない）。保存の版もここから
   */
  const editBase = useRef<GroupDetail | null>(null);
  useEffect(() => {
    const prev = editBase.current;
    if (!prev || prev === g) return;
    editBase.current = g;
    editInit.current = editFormOf(g);
    setEdit((x) => (x ? rebaseFields(editFormOf(prev), x, editFormOf(g)) : x));
  }, [g]);
  const startEdit = () => {
    const init = editFormOf(g);
    editBase.current = g;
    editInit.current = init;
    setEdit(init);
    setSaveErr(undefined);
    // 開いたときに引いた顧客の情報が古ければ取り直す（届くまでは今の情報で欄を出す＝形は変わらない）
    if (pq.data) void pq.refetch();
    setTimeout(() => document.getElementById("ge_name")?.focus(), 0);
  };
  const setE = (p: Partial<EditForm>) => setEdit((x) => (x ? { ...x, ...p } : x));

  const save = async (siteGeoFollow?: boolean) => {
    if (!edit) return;
    if (!edit.name.trim()) {
      setSaveErr(t(GROUP_MSG.nameRequired));
      return;
    }
    // #132：住所が変わり、手で直したピンがあるときは先に聞く（docs/49 §2.3）
    if (siteGeoFollow === undefined && g.siteGeo?.source === "manual" && edit.site.trim() !== (g.siteAddress ?? "")) {
      setFollowAsk(true);
      return;
    }
    setBusy(true);
    setSaveErr(undefined);
    try {
      const bodyOf = (f: EditForm, version: string): GroupUpdate => ({
        name: f.name.trim(),
        partnerBranchId: f.branchId,
        partnerContactId: f.contactId,
        staffName: f.staff.trim(),
        paymentTerms: f.terms,
        siteAddress: f.site.trim(),
        ownerName: f.owner.trim(),
        billingTiming: f.timing,
        // 請求の形（#267）：sales の会社だけ送る（null＝{customer}の設定に従う）
        ...(sales ? { billingMode: f.billingMode } : {}),
        ...(siteGeoFollow === undefined ? {} : { siteGeoFollow }),
        // 継続案件（#289）：オンの会社だけ送る（オフの会社は今の値のまま）。開始日は継続のときだけ（空なら API が今日）
        ...(og.on ? { isOngoing: f.ongoing, ...(f.ongoing && f.startedOn ? { startedOn: f.startedOn } : {}) } : {}),
        // 社内メモは別の枠（追記の口）で保存する（ここでは触らない）
        version,
      });
      // #202：欄の元の版を付けて送る。ほかで変わっていたら（409 GROUP_STALE）最新を読み、この画面で修正した欄だけを重ねて送り直す
      const base = editBase.current ?? g;
      let next: GroupDetail;
      let merged = false;
      try {
        next = await api<GroupDetail>("PUT", `/groups/${g.id}`, bodyOf(edit, base.updatedAt));
      } catch (e) {
        if (!(e instanceof ApiError && e.code === "GROUP_STALE")) throw e;
        const latest = await api<GroupDetail>("GET", `/groups/${g.id}`);
        next = await api<GroupDetail>("PUT", `/groups/${g.id}`, bodyOf(rebaseFields(editFormOf(base), edit, editFormOf(latest)), latest.updatedAt));
        merged = true;
      }
      editBase.current = null;
      refresh(next);
      setEdit(null);
      toast(merged ? CONFLICT_MSG.autoMerged : t(GROUP_MSG.saved));
    } catch (e) {
      const fe = e instanceof ApiError ? e.problem.errors : undefined;
      setSaveErr(fe ? String(Object.values(fe)[0]) : messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  /** 見積を追加：すぐ下書きへ。支店・顧客担当者・担当者・工事場所・工事名は API が案件から決める */
  const addJob = async () => {
    setBusy(true);
    try {
      const e = await api<Estimate>("POST", "/estimates", { clientId: g.client.id, projectGroupId: g.id });
      refresh();
      void qc.invalidateQueries({ queryKey: ["estimates"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      nav(`/estimates/${e.id}`, { state: { fresh: true, notice: fill(GROUP_MSG.jobAdded) } });
    } catch (e) {
      toast(messageOf(e), undefined, "error");
      setBusy(false);
    }
  };

  const setDone = async (done: boolean) => {
    setBusy(true);
    try {
      const next = await api<GroupDetail>("POST", `/groups/${g.id}/${done ? "complete" : "reopen"}`);
      refresh(next);
      toast(fill(done ? GROUP_MSG.completed : GROUP_MSG.reopened), done ? { label: "元に戻す", onClick: () => void setDone(false) } : undefined);
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    } finally {
      setBusy(false);
    }
  };

  /**
   * 消す（見積が0枚のときだけ）。一覧へ移って、トーストの「元に戻す」で同じ中身の案件を作り直せる。
   * #265 → #271：ゴミ箱の見積書（番号の有無を問わず）も一緒に完全に削除したときは作り直せない＝「元に戻す」を出さない
   */
  const [askDelete, setAskDelete] = useState(false);
  /** 配備の切り替わりで前の API が答えたときは trash が無い＝0件として扱う */
  const trash = g.trash ?? { count: 0, numbers: [] };
  const remove = async () => {
    const purging = trash.count > 0;
    setAskDelete(false);
    setBusy(true);
    try {
      await api<void>("DELETE", `/groups/${g.id}`);
      qc.removeQueries({ queryKey: ["group", g.id] });
      void qc.invalidateQueries({ queryKey: ["group-list"] });
      void qc.invalidateQueries({ queryKey: ["groups", g.client.id] });
      if (purging) {
        void qc.invalidateQueries({ queryKey: ["estimates-trash"] });
        void qc.invalidateQueries({ queryKey: ["estimates"] });
        nav("/groups", { replace: true, state: { notice: fill(GROUP_MSG.deleted) } });
        return;
      }
      const again: GroupCreate = {
        clientId: g.client.id,
        name: g.name,
        partnerBranchId: g.branch?.id ?? null,
        partnerContactId: g.contact?.id ?? null,
        staffName: g.staffName,
        paymentTerms: g.paymentTerms.source === "group" ? g.paymentTerms.terms : null,
        memo: g.memo,
        siteAddress: g.siteAddress ?? "",
        ownerName: g.ownerName ?? "",
        billingTiming: g.billingTiming,
      };
      nav("/groups", { replace: true, state: { notice: fill(GROUP_MSG.deleted), recreate: again } });
    } catch (e) {
      toast(messageOf(e), undefined, "error");
      setBusy(false);
    }
  };

  const hasJobs = g.jobs.length > 0;
  const termsSrc = g.paymentTerms.source === "group" ? `この${L.project}だけ` : g.paymentTerms.source === "client" ? `${L.customer}の支払条件に従う` : "既定";
  /** 見積／請求書のタブ。URL の ?tab=invoices で覚える（請求書を開いて戻っても同じタブ） */
  const [sp, setSp] = useSearchParams();
  type Tab = "quotes" | "dnotes" | "invoices" | "recurring" | "schedule" | "payments" | "vendor" | "orders" | "purchases" | "deliveries";
  /** 工程表（#100）：モジュール schedule の会社だけ（Me.features.schedule＝API の scheduleAvailable と同じ。#142 段5 から製品は見ない） */
  const sched = !!meQ.data?.features.schedule;
  /** 外注見積のタブ：使う機能「{vendorQuote}の取込」か「見積依頼」（#149）の会社だけ。見積依頼だけの会社はタブの名前が「見積依頼」 */
  const vq = uses(meQ.data, "vendorQuote");
  const rfqOn = uses(meQ.data, "rfq");
  /** #198（docs/79 §11.1 ラ）：{vendorQuote}は一覧（/vendor-quotes?group=）へ移る道にし、見積依頼は独立したタブ「見積依頼」（使う機能 rfq の会社だけ） */
  const vtab = rfqOn;
  /** {order}（#233、docs/93 §4.1）：モジュール sales の会社だけ。数字は中身（GroupOrders）と同じ問い合わせの「すべて」 */
  const ordersOn = !!meQ.data?.tenant.modules.includes("sales");
  /** {deliveryNote}（#266、docs/96）：モジュール sales かつ使う機能「{deliveryNote}」の会社だけ。見積と請求のあいだ */
  const dnOn = ordersOn && uses(meQ.data, "deliveryNote");
  /** 定期請求（#268）：sales の会社で、請求タイミングが定期の{project}だけ。数は請求履歴の枚数 */
  const rcOn = ordersOn && g.billingTiming === "recurring";
  const rcQ = useGroupRecurring(g.id, rcOn);
  const tab: Tab = sp.get("tab") === "invoices" ? "invoices" : sp.get("tab") === "recurring" && rcOn ? "recurring" : sp.get("tab") === "dnotes" && dnOn ? "dnotes" : sp.get("tab") === "schedule" && sched ? "schedule" : sp.get("tab") === "vendor" && vtab ? "vendor" : sp.get("tab") === "payments" && pays ? "payments" : sp.get("tab") === "orders" && ordersOn ? "orders" : sp.get("tab") === "purchases" && ordersOn ? "purchases" : sp.get("tab") === "deliveries" ? "deliveries" : "quotes";
  const setTab = (k: Tab) => {
    const next = new URLSearchParams(sp);
    if (k !== "quotes") next.set("tab", k);
    else next.delete("tab");
    setSp(next, { replace: true });
  };
  /** ←→ でタブを移る（WAI-ARIA のタブの作法） */
  const TABS: Tab[] = ["quotes", ...(dnOn ? (["dnotes"] as const) : []), "invoices", ...(rcOn ? (["recurring"] as const) : []), ...(sched ? (["schedule"] as const) : []), ...(pays ? (["payments"] as const) : []), ...(vtab ? (["vendor"] as const) : []), ...(ordersOn ? (["orders", "purchases"] as const) : []), "deliveries"];
  /** 送付の記録（#223 の2回目、docs/89 §6）：この{project}の帳票・見積依頼・受け取った書類を同封した送付（{project}をまたぐ束ねも） */
  const dlv = useDeliveries({ group: g.id });
  const onTabKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const i = TABS.indexOf(tab);
    const k = TABS[(i + (e.key === "ArrowRight" ? 1 : TABS.length - 1)) % TABS.length]!;
    setTab(k);
    document.getElementById(`gtab-${k}`)?.focus();
  };
  /** 外注見積（047）：一覧への道（#198）の数字だけ読む（削除済みは数えない・不採用は数える＝一覧の「すべて」と同じ） */
  const vqList = useQuery({ queryKey: ["vendor-quotes", g.id], queryFn: () => api<VendorQuoteList>("GET", `/groups/${g.id}/vendor-quotes`), enabled: vq });
  const vqCount = vqList.data?.items.length ?? 0;
  /** 見積依頼だけの会社（#149）：タブの数字は見積依頼の数（中身の GroupRfqs と同じ問い合わせ）。
   *  #179：外注見積もある会社も先に読む（タブの中で、見積依頼の表が後から入って「外注見積を登録」がずれないように） */
  // タブの中身（GroupRfqs）と同じ問い合わせ（すべて）＝タブの数とチップの「すべて」が同じ
  const rfqList = useQuery({ queryKey: ["rfqs", "group", g.id, "all"], queryFn: () => api<RfqList>("GET", `/rfqs?f=all&g=${g.id}`), enabled: rfqOn });
  const orderList = useQuery({ queryKey: ["orders", "all", "", g.id], queryFn: () => api<OrderList>("GET", `/orders?tab=all&group=${g.id}`), enabled: ordersOn });
  /** 仕入（#233 3a）：タブの数字は中身（GroupPurchases）と同じ問い合わせの「すべて」 */
  const purchaseList = useQuery({ queryKey: ["purchases", "all", "", g.id], queryFn: () => api<PurchaseList>("GET", `/purchases?tab=all&group=${g.id}`), enabled: ordersOn });
  /** {deliveryNote}（#266）：タブの数字は中身（GroupDeliveryNotes）と同じ問い合わせの「すべて」 */
  const dnList = useQuery({ queryKey: ["delivery-notes", "all", "", g.id], queryFn: () => api<DeliveryNoteList>("GET", `/delivery-notes?tab=all&group=${g.id}`), enabled: dnOn });
  /** タブの下線（.ind。9/29）：選んだタブの下へ 220ms ですべる */
  const gtabsRef = useRef<HTMLDivElement>(null);
  const gindRef = useRef<HTMLSpanElement>(null);
  useTabIndicator(gtabsRef, gindRef, `${tab}|${g.jobs.length}|${vqCount}|${pays ? 1 : 0}|${sched ? 1 : 0}|${g.schedule?.taskCount ?? 0}|${dlv.data?.items.length ?? 0}|${orderList.data?.counts.all ?? 0}|${purchaseList.data?.counts.all ?? 0}|${rcOn ? (rcQ.data?.history.length ?? -1) : -2}`);
  /** この案件の請求書すべて（見積の並び → その見積の中は古い順） */
  const invoices = g.jobs.flatMap((job) => job.invoices.map((inv) => ({ inv, job })));
  /**
   * 案件詳細の「？」（#92）：欄の添え書き・帯の後半・「見積追加」の説明をここにまとめる（前はタブの行の「？」にあった addJobNote も）。
   * 直しているときだけ 支店・{customer}担当者・施主名 の説明を足す
   */
  const contentHelp = [
    `修正しても、発行済みの見積書・請求書は変わりません。支店・${L.customer}担当者・担当者・${L.siteAddress}は、新しく作る見積に写ります。支払条件をこの${L.project}だけ変えると、お支払期限の決まりだけが変わります（消費税の丸め・どちらを適格請求書にするかは${L.customer}の設定のまま）。`,
    edit ? `${partyFieldsHelp(L)}${field ? "施主名は下請けのときに入れます（暗号化して保存します）。" : ""}請求タイミングの一括＝受注した見積1枚につき請求書1枚。分割＝${installmentDefaultNames(product, instDefaults)}のように何回かに分けて請求します（回ごとの予定は見積ごとに作ります）。「都度／締め」は${L.customer}の支払条件で、これとは別の話です。` : "",
    g.billingTiming === "split" && !edit ? "分割なので、受注した見積ごとに分割の予定を作ります。" : "",
    g.jobs.some((j) => j.plan) ? "分割の請求書は1回ずつ作成します。現在の回を発行すると、次の回を作成できます。" : "",
    hasJobs ? t(GROUP_MSG.addJobNote) : "",
    hasJobs || g.completedAt ? "完了にすると、一覧の「進行中」から外れます（あとで戻せます）。完了にしたあとも、見積を追加すると進行中に戻ります。" : "",
  ].join("");
  const jobChip = (j: GroupJob) =>
    j.status === "ordered" ? <span className="chip ai">受注</span> : j.status === "draft" ? <span className="chip">下書き</span> : j.status === "lost" ? <span className="chip">失注</span> : j.status === "billed" ? "請求済" : "未受注";

  return (
    <>
      <BackButton back={back} />
      {back.dialog}
      <div className="hrow ghead">
        <h1>
          {g.name}{" "}
          {/* 状態は見出しの右のチップ（#168：「すべて請求済み」「○/○ に完了」の帯をやめた。完了は日付も） */}
          {asOngoing ? <span className="chip ai" data-testid="group-ongoing-chip">{og.short}</span> : null}{asOngoing ? " " : null}
          {g.completedAt ? (
            <span className="chip" title={t(GROUP_MSG.isCompleted).replace("{date}", md(g.completedAt.slice(0, 10)))} data-testid="group-completed-band">
              {GROUP_STATUS_LABEL.done} {md(g.completedAt.slice(0, 10))}
            </span>
          ) : asOngoing ? null : (
            <StatusChip status={g.status} />
          )}
        </h1>
        <span className="sp" />
        {g.completedAt ? (
          <button type="button" className="small" disabled={busy} onClick={() => void setDone(false)} data-testid="group-reopen">進行中に変更</button>
        ) : hasJobs || asOngoing ? (
          <button type="button" className="small" disabled={busy} onClick={() => void setDone(true)} data-testid="group-complete">完了</button>
        ) : null}
        <button className="btn primary" type="button" disabled={busy} onClick={() => void addJob()} data-testid="group-add-job">
          <Icon k="plus" />
          {hasJobs ? "見積追加" : "見積作成"}
        </button>
        {/* 単体送付状（#223、docs/89 §5.3・§15 の12）：PC だけ */}
        {!pcOnly.hidden("/cover-letters/new") ? (
          <RowMenu label={`${g.name}のほかの動き`} testId="group-more" items={[{ label: COVER_LETTER_MSG.create, to: coverNewTo, testId: "group-cover-letter" }]} />
        ) : null}
      </div>

      {/* 数字の枠4つ（ホームの「今月の数字」と同じ部品・言い方。docs/25 §3-5。案件では低い枠、説明は「？」）。
          入金（sales）の会社は「未入金」を足して5つ並び（どの枠も2行のまま＝低い枠。期限超過があるときだけ3行目） */}
      <div className={"mtiles compact" + (pays ? " five" : "") + (s.margin ? " six" : "")} style={{ marginTop: 0 }} data-testid="group-summary">
        <div className="mtile" data-testid="gtile-受注">
          <div className="t">受注</div>
          <div className="v"><b className="num">{s.ordered.count}</b> 件{s.ordered.count ? <span className="muted">　<span className="num">{yen(s.ordered.total)}</span> 円</span> : null}</div>
        </div>
        <div className="mtile" data-testid="gtile-請求済">
          <div className="t">請求済</div>
          <div className="v"><b className="num">{s.billed.count}</b> 件{s.billed.count ? <span className="muted">　<span className="num">{yen(s.billed.total)}</span> 円</span> : null}</div>
        </div>
        {pays && s.unpaid ? (
          /* E1：未入金（/home と同じ式） */
          <div className="mtile" data-testid="gtile-未入金">
            <div className="t">未入金</div>
            <div className="v"><b className="num">{s.unpaid.count}</b> 件{s.unpaid.count ? <span className="muted">　<span className="num">{yen(s.unpaid.total)}</span> 円</span> : null}</div>
            {s.unpaid.overdueTotal ? <div className="hint shu">うち期限超過 <span className="num">{yen(s.unpaid.overdueTotal)}</span> 円</div> : null}
          </div>
        ) : null}
        {s.margin ? (
          /* 仕入と粗利（#233 3a、docs/93 §15.10 ③・③-2）：sales の会社は「利益」の枠の代わりに 仕入（税込）と 粗利（税抜。仕入が入るまで「—」） */
          <>
            <div className="mtile" data-testid="gtile-仕入">
              <div className="t">{PURCHASE_MSG.tilePurchases}<Help testId="help-purchases-tile" text={t(PURCHASE_MSG.helpPurchases)} /></div>
              {s.margin.purchases.count ? (
                <>
                  <div className="v"><b className="num">{s.margin.purchases.count}</b> 件<span className="muted">　<span className="num">{yen(s.margin.purchases.total)}</span> 円</span></div>
                  {s.margin.purchases.overdueTotal ? <div className="hint shu">うち期限超過 <span className="num">{yen(s.margin.purchases.overdueTotal)}</span> 円</div> : null}
                </>
              ) : s.margin.orders.withoutPurchase ? (
                <>
                  <div className="v muted" data-testid="gtile-orders-without">{t(PURCHASE_MSG.ordersWithout(s.margin.orders.withoutPurchase, yen(s.margin.orders.withoutPurchaseTotal)))}</div>
                  <div className="hint shu">{PURCHASE_MSG.ordersWithoutNote}</div>
                </>
              ) : (
                <div className="v"><span className="shu">{PURCHASE_MSG.purchasesMissing}</span></div>
              )}
            </div>
            <div className="mtile" data-testid="gtile-粗利">
              <div className="t">{PURCHASE_MSG.tileMargin}<Help testId="help-margin-tile" text={<>{t(PURCHASE_MSG.helpMargin)}{PURCHASE_MSG.helpFiguresTax}{figuresNote}</>} /></div>
              {s.margin.reliable && s.margin.margin != null ? (
                <div className="v"><b className="num">{yen(s.margin.margin)}</b> 円{s.margin.rate != null ? <span className="muted">（{s.margin.rate}%）</span> : null}</div>
              ) : (
                <div className="v muted">—</div>
              )}
            </div>
          </>
        ) : (
        <div className="mtile" data-testid="gtile-利益">
          <div className="t">利益（受注した分）<Help testId="help-profit" text={<>受注した見積の数字です。金額は税込、利益だけ税抜。利益は、受注した見積の仕入れが全部入っているときだけ出します（「仕入れ未入力」のあいだは出しません）。{figuresNote}</>} /></div>
          {s.profit.missing > 0 ? (
            <div className="v"><span className="shu">仕入れ未入力 {s.profit.missing}枚</span></div>
          ) : s.profit.amount != null ? (
            <div className="v"><b className="num">{yen(s.profit.amount)}</b> 円{s.profit.rate != null ? <span className="muted">（{s.profit.rate}%）</span> : null}</div>
          ) : (
            <div className="v muted">—</div>
          )}
        </div>
        )}
        <div className="mtile" data-testid="gtile-未受注">
          <div className="t">未受注</div>
          <div className="v"><b className="num">{s.waiting.count}</b> 件{s.waiting.count ? <span className="muted">　<span className="num">{yen(s.waiting.total)}</span> 円</span> : null}{s.lost.count ? <span className="muted">　失注 {s.lost.count}件</span> : null}</div>
        </div>
      </div>

      {/* 案件詳細（全幅。#89 で「案件内容」から）。「直す」でその場の欄に。説明は「？」 */}
      <section className="paper gcontent" data-testid="group-side">
        <div className="row" style={{ marginBottom: 6 }}>
          <h2 style={{ margin: 0 }}>{L.project}詳細<Help testId="help-content" text={contentHelp} /></h2>
          <span className="sp" />
          {edit ? null : (
            <button type="button" className="small" onClick={startEdit} data-testid="group-edit">修正</button>
          )}
        </div>
        {edit ? (
          <div className="gform" style={{ padding: 0, maxWidth: "none", gap: 16 }}>
            <div className="two">
              <div className="fld">
                <label className="lbl" htmlFor="ge_name">{L.project}名<span className="req">必須</span></label>
                <input id="ge_name" className="inp" value={edit.name} onChange={(e) => setE({ name: e.target.value })} />
                {g.nameFollowsSubject && g.jobs.length === 1 ? <span className="hint">名前を修正すると、以後は{L.subject}に合わせて変わりません</span> : null}
              </div>
              <div className="fld">
                <label className="lbl" htmlFor="ge_staff">自社の担当者</label>
                <input id="ge_staff" className="inp" value={edit.staff} onChange={(e) => setE({ staff: e.target.value })} />
              </div>
            </div>
            {og.on ? (
              <div className="two" data-testid="group-edit-ongoing">
                <div className="fld">
                  <span className="lbl">{L.ongoingProject}</span>
                  <Switch checked={edit.ongoing} onChange={(v) => setE({ ongoing: v })} label={og.short} testId="ge-ongoing" />
                </div>
                {edit.ongoing ? (
                  <div className="fld">
                    <label className="lbl" htmlFor="ge_started">{ONGOING_MSG.startedOn}</label>
                    <DateInput id="ge_started" label={ONGOING_MSG.startedOn} value={edit.startedOn} onChange={(v) => setE({ startedOn: v })} testId="ge-started" />
                  </div>
                ) : null}
              </div>
            ) : null}
            {partner ? (
              <div className="two">
                <BranchSelect partner={partner} id="ge_branch" value={edit.branchId} onChange={(v) => setE({ branchId: v })} />
                <ContactSelect partner={partner} branchId={edit.branchId} id="ge_contact" value={edit.contactId} onChange={(v) => setE({ contactId: v })} onPartner={setPartnerOverride} />
              </div>
            ) : pq.isError ? (
              <ErrorText>{messageOf(pq.error)}</ErrorText>
            ) : (
              // 読み込み中（#179）：届いたあとと同じ形の欄を押せない形で出す＝下の 請求タイミング・保存 が届いた瞬間にずれない（押し間違えない）。
              // 支店の欄は この案件に支店があるときだけ（支店のある{customer}かは届くまで分からない。上の pq は画面を開いたときに引いておく）
              <div className="two" aria-busy="true" data-testid="group-edit-loading">
                {g.branch ? (
                  <div className="fld">
                    <label className="lbl" htmlFor="ge_branch">支店・営業所</label>
                    <select id="ge_branch" className="inp" disabled>
                      <option>{g.branch.name}</option>
                    </select>
                  </div>
                ) : null}
                <div className="fld">
                  <label className="lbl" htmlFor="ge_contact">{L.customer}担当者</label>
                  <select id="ge_contact" className="inp" disabled>
                    <option>読み込んでいます…</option>
                  </select>
                </div>
              </div>
            )}
            {partner || !pq.isError ? (
              <TermsChooser
                // 届くまでは 案件の今の支払条件の由来から分かる分だけ（{customer}に従う案件なら同じ条件）。押せない形
                clientTerms={(partner ? (partner.paymentTerms ?? null) : g.paymentTerms.source === "client" ? g.paymentTerms.terms : null) as PaymentTerms | null}
                value={edit.terms}
                onChange={(v) => setE({ terms: v })}
                idBase="ge_terms"
                disabled={!partner}
              />
            ) : null}
            <div className="fld">
              <span className="lbl">請求タイミング</span>
              <span className="seg fit" role="group" aria-label="請求タイミング">
                {(sales ? (["lump", "split", "recurring"] as const) : (["lump", "split"] as const)).map((k) => (
                  <button key={k} type="button" aria-pressed={edit.timing === k} onClick={() => setE({ timing: k })} data-testid={`group-timing-${k}`}>
                    {BILLING_TIMING_LABEL[k]}
                  </button>
                ))}
              </span>
            </div>
            {/* 請求方式（#267、Figma ②-3 278:150）：{customer}の設定｜都度｜締め。sales の会社だけ */}
            {sales ? (
              <div className="fld">
                <span className="lbl" id="ge_bm_l">
                  {CLOSING_MSG.billingMode}
                  <Help testId="help-group-billing-mode" label={CLOSING_MSG.billingMode} text={t(CLOSING_MSG.groupHelp)} />
                </span>
                <span className="seg fit" role="group" aria-labelledby="ge_bm_l">
                  {([null, "per_delivery", "closing"] as const).map((k) => (
                    <button key={k ?? "follow"} type="button" aria-pressed={edit.billingMode === k} onClick={() => setE({ billingMode: k })} data-testid={`group-billing-mode-${k ?? "follow"}`}>
                      {k ? BILLING_MODE_LABEL[k] : t(CLOSING_MSG.groupFollow(BILLING_MODE_LABEL[g.billingModeOfClient]))}
                    </button>
                  ))}
                </span>
              </div>
            ) : null}
            <div className="two">
              <div className="fld">
                <label className="lbl" htmlFor="ge_site">{L.siteAddress}</label>
                <input id="ge_site" className="inp" value={edit.site} onChange={(e) => setE({ site: e.target.value })} />
              </div>
              {field ? (
                <div className="fld">
                  <label className="lbl" htmlFor="ge_owner">施主名</label>
                  <input id="ge_owner" className="inp" value={edit.owner} onChange={(e) => setE({ owner: e.target.value })} />
                </div>
              ) : null}
            </div>
            <ErrorText>{saveErr}</ErrorText>
            <div className="row" style={{ alignItems: "center" }}>
              <span className="sp" />
              <button type="button" className="btn quiet" disabled={busy} onClick={() => { setEdit(null); setSaveErr(undefined); }}>取消</button>
              <button type="button" className="btn primary" disabled={busy} onClick={() => void save()} data-testid="group-save">{L.project}保存</button>
            </div>
          </div>
        ) : (
          <>
            <dl className="kv gkv">
              <dt>{L.customer}</dt>
              <dd>
                <Link to={`/partners/${g.client.id}`}>{g.client.name}</Link> <span className="muted">{g.client.honorific}</span>
              </dd>
              <dt>担当者</dt>
              <dd>{g.staffName || <span className="muted">—</span>}</dd>
              {asOngoing ? (
                <>
                  <dt>{ONGOING_MSG.startedOn}</dt>
                  <dd className="num" data-testid="group-started-on">{g.startedOn ? ymdShort(g.startedOn) : <span className="muted">—</span>}</dd>
                </>
              ) : null}
              <dt>支店</dt>
              <dd>{g.branch ? g.branch.name : <span className="muted">本社</span>}</dd>
              <dt>{L.customer}担当者</dt>
              <dd>{g.contact ? `${g.contact.department ? `${g.contact.department} ` : ""}${g.contact.name} 様` : <span className="muted">指定しない</span>}</dd>
              <dt>支払条件</dt>
              <dd data-testid="group-terms">
                {paymentTermsLabel(g.paymentTerms.terms)} <span className="src">（{termsSrc}）</span>
              </dd>
              <dt>請求タイミング</dt>
              <dd data-testid="group-timing">{BILLING_TIMING_LABEL[g.billingTiming]}</dd>
              {sales ? (
                <>
                  <dt>{CLOSING_MSG.billingMode}</dt>
                  <dd data-testid="group-billing-mode">
                    {BILLING_MODE_LABEL[g.billingMode ?? g.billingModeOfClient]}{" "}
                    <span className="src">{t(g.billingMode ? CLOSING_MSG.groupOwnSrc : CLOSING_MSG.groupFollowSrc)}</span>
                  </dd>
                </>
              ) : null}
              <dt>{L.siteAddress}</dt>
              <dd>{g.siteAddress || <span className="muted">—</span>}</dd>
              {field && g.ownerName ? (
                <>
                  <dt>施主</dt>
                  <dd>{g.ownerName} 様</dd>
                </>
              ) : null}
            </dl>
            {/* 現場の地図（#132、docs/49 §2）：住所の下。ピンの手直し・現在地・経路を開く・地図の種類。使う機能「地図」の会社だけ（#142 段3。住所は残る） */}
            {uses(meQ.data, "map") ? (
              // #235：開閉。畳んでいる間は地図の部品を読まない（見出しの1行 48px だけ）
              !mapFold.open ? (
                <SiteMapFolded g={g} fold={mapFold} />
              ) : (
                // 地図の部品を待つ間は、地図と同じ高さの代わり（#179：高さが無いと地図が出た瞬間に下の欄が 350〜410px ずれて押し間違える）
                <Suspense fallback={<SiteMapLoading g={g} provider={meQ.data?.features.map.provider ?? "gsi"} canReverse={!!meQ.data?.features.map.reverse} fold={mapFold} />}>
                  {meQ.data?.features.map.provider === "google" ? <GoogleSiteMap g={g} onSaved={refresh} fold={mapFold} /> : <SiteMap g={g} onSaved={refresh} fold={mapFold} />}
                </Suspense>
              )
            ) : null}
          </>
        )}
      </section>
      {asOngoing ? <GroupMonthlyTable id={g.id} /> : null}
      {followAsk ? (
        <SiteGeoFollowDialog
          onAnswer={(follow) => {
            setFollowAsk(false);
            void save(follow);
          }}
        />
      ) : null}
      {/* #265 → #271：ゴミ箱の見積書も一緒に完全に削除する確認（番号ありがあれば窓に番号＝欠番になる） */}
      {askDelete ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && setAskDelete(false)}>
          <div className="dlg" role="alertdialog" aria-label={t(GROUP_MSG.deleteTitle)} onKeyDown={(e) => e.key === "Escape" && setAskDelete(false)} data-testid="group-delete-dialog">
            <div className="dh">{t(GROUP_MSG.deleteTitle)}</div>
            <div className="db">
              <p style={{ margin: 0 }} data-testid="group-delete-ask">{trash.numbers.length
                  ? fill(GROUP_MSG.deleteWithTrashNumbered).replace("{n}", String(trash.count)).replace("{numbers}", trash.numbers.join("、"))
                  : fill(GROUP_MSG.deleteWithTrash).replace("{n}", String(trash.count))}</p>
            </div>
            <div className="df">
              <button type="button" className="btn primary" autoFocus onClick={() => setAskDelete(false)}>取消</button>
              <button type="button" className="btn shu" onClick={() => void remove()} data-testid="group-delete-confirm">削除</button>
            </div>
          </div>
        </div>
      ) : null}

      {/* 受け取った書類（部品 received、../eigyouryoku/docs/13）：案件詳細と社内メモのあいだ。使う機能 receivedDocs の会社だけ */}
      {meQ.data ? <Slot name="group.received" me={meQ.data} group={g} /> : null}

      {/* 社内メモ（独立した枠。打つと自動で保存） */}
      <MemoBox g={g} onSaved={refresh} />

      {/* 見積／請求／外注のタブ（#89 で短く） */}
      <div className="gtabsrow">
      <div className="gtabs" role="tablist" aria-label={`${L.project}の見積と請求書${rfqOn ? `と${RFQ_MSG.listTitle}` : ""}`} ref={gtabsRef}>
        {(
          [
            ["quotes", "見積", g.jobs.length],
            ...(dnOn ? ([["dnotes", t(DELIVERY_NOTE_MSG.groupTab), dnList.data?.counts.all ?? 0]] as const) : []),
            ["invoices", "請求", invoices.length],
            ...(rcOn ? ([["recurring", RECURRING_MSG.tab, rcQ.data ? rcQ.data.history.length : null]] as const) : []),
            ...(sched ? ([["schedule", L.schedule, g.schedule?.taskCount ?? 0]] as const) : []),
            ...(pays ? ([["payments", "入金", g.payments?.length ?? 0]] as const) : []),
            ...(rfqOn ? ([["vendor", RFQ_MSG.listTitle, rfqList.data?.counts.all ?? 0]] as const) : []),
            ...(ordersOn ? ([["orders", L.order, orderList.data?.counts.all ?? 0], ["purchases", PURCHASE_MSG.groupTab, purchaseList.data?.counts.all ?? 0]] as const) : []),
            ["deliveries", COVER_LETTER_MSG.deliveriesTab, dlv.data?.items.length ?? 0],
          ] as const
        ).map(([k, label, n]) => (
          <button
            key={k}
            type="button"
            role="tab"
            id={`gtab-${k}`}
            aria-controls={`gpanel-${k}`}
            aria-selected={tab === k}
            tabIndex={tab === k ? 0 : -1}
            className={"gtab" + (tab === k ? " on" : "")}
            onClick={() => setTab(k)}
            onKeyDown={onTabKey}
            data-testid={`group-tab-${k}`}
          >
            {label} {n == null ? null : <span className="num">{n}</span>}
          </button>
        ))}
        <span className="sp" />
        <span className="ind" ref={gindRef} aria-hidden="true" />
      </div>
      {/* #198：{vendorQuote}は同じ一覧をこの{project}で絞って開く（画面は1つ。docs/79 決定1・§2 レ） */}
      {vq ? (
        <Link className="gtab gtab-link" to={`/vendor-quotes?group=${g.id}&via=group`} data-testid="group-vendor-quotes">
          {L.vendorQuote} <span className="num">{vqList.data ? vqCount : "…"}</span>
          <span aria-hidden="true">›</span>
        </Link>
      ) : null}
      </div>
      {tab === "quotes" ? (
        <div role="tabpanel" id="gpanel-quotes" aria-labelledby="gtab-quotes" className="gpanel">
          {!hasJobs ? (
            <div className="gempty">
              <p style={{ margin: "0 0 10px" }}>{t(GROUP_MSG.noJobs)}</p>
              {trash.count > 0 ? (
                <p style={{ margin: "0 0 10px" }}>
                  <Link className="btn text" to="/estimates?f=trash" data-testid="group-trash-link">
                    {TRASH_MSG.groupTrashLink.replace("{n}", String(trash.count))} <span aria-hidden="true">›</span>
                  </Link>
                </p>
              ) : null}
              <div className="row">
                <button type="button" className="btn primary" disabled={busy} onClick={() => void addJob()}>見積作成</button>
                <span className="sp" />
                <button
                  type="button"
                  className="btn text"
                  disabled={busy}
                  onClick={() => (trash.count > 0 ? setAskDelete(true) : void remove())}
                  data-testid="group-delete"
                >
                  {L.project}削除
                </button>
              </div>
            </div>
          ) : narrow ? (
            <ul className="gcards" data-testid="group-jobs-cards">
              {g.jobs.map((j) => (
                <li key={j.id}>
                  <Link className="gcard" to={`/estimates/${j.id}`} data-testid="group-job-card">
                    <span className="gc-no">
                      {j.displayNo ? <span className="num">{j.displayNo}</span> : <span className="muted gc-nono">未発行</span>}
                      {j.draftRevisionNo != null ? <span className="chip" title={`未発行の ${j.draftRevisionNo}回目があります`}>{j.draftRevisionNo}回目 未発行</span> : null}
                    </span>
                    <span className="gc-subj">{j.subject || <span className="muted">（{L.subject}はまだ）</span>}</span>
                    <span className="gc-foot">
                      <span className="gc-st">
                        {jobChip(j)}
                        {j.plan ? <PlanProgress plan={j.plan} /> : null}
                      </span>
                      <span className="gc-amt">{j.total ? <><span className="gc-lbl">税込</span> <span className="num">{yen(j.total)} 円</span></> : <span className="muted">—</span>}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <>
              <div className="tbl-wrap">
                <table className="list" data-testid="group-jobs">
                  <thead>
                    <tr>
                      <th>番号</th>
                      <th>{L.subject}</th>
                      <th>いまの状態</th>
                      <th className="amt">金額（税込）</th>
                      <th>日付</th>
                      <th>請求書</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.jobs.map((j) => (
                      <Fragment key={j.id}>
                        <GroupJobRow
                          j={j}
                          chip={jobChip(j)}
                          timing={g.billingTiming}
                          onOpen={() => nav(`/estimates/${j.id}`)}
                          onInvoice={() => void createInvoice(j.id)}
                          onInstallment={(instId) => void createInvoice(j.id, instId)}
                          onPlanNew={() => setPlanFor(j.id)}
                        />
                        {j.plan && planFor !== j.id
                          ? [
                              ...j.plan.items.map((it) => (
                                <tr key={`${j.id}-${it.seq}`} className="sub" data-testid="plan-row">
                                  <td />
                                  <td className="inst" colSpan={2}>
                                    └ <b>{it.seq}回目 {it.label}</b>　{it.kind === "rest" ? "残り" : it.kind === "percent" ? `${it.percent}%` : `税込 ${yen(it.amountIncl ?? 0)} 円`}
                                    {it.plannedOn ? <span className="muted">　予定 {md(it.plannedOn)}</span> : null}
                                  </td>
                                  <td className="amt num"><AmtCell inc={it.total} ex={it.subtotal} /></td>
                                  <td colSpan={2}>
                                    {it.invoice ? (
                                      <>
                                        <Link className="btn text num" to={`/invoices/${it.invoice.id}`}>{it.invoice.documentNo ?? "（下書き）"}</Link>
                                        <span className="muted">　{it.invoice.status === "draft" ? "下書き" : "出した"}</span>
                                      </>
                                    ) : (
                                      <span className="muted">まだ</span>
                                    )}
                                  </td>
                                </tr>
                              )),
                              <tr key={`${j.id}-edit`} className="sub">
                                <td />
                                <td className="inst" colSpan={5}>
                                  <button type="button" className="btn text" onClick={() => setPlanFor(j.id)} data-testid="plan-edit">分割予定修正</button>
                                </td>
                              </tr>,
                            ]
                          : null}
                        {planFor === j.id ? (
                          <tr className="sub">
                            <td colSpan={6} style={{ padding: 0 }}>
                              <BillingPlanEditor
                                quoteId={j.id}
                                plan={j.plan}
                                draft={defaultInstallments(new Date().toISOString().slice(0, 10), product, instDefaults)}
                                onSaved={(p: BillingPlan) => {
                                  setPlanFor(null);
                                  refresh();
                                  toast(`分割の予定を保存しました（${p.items.length}回）`);
                                }}
                                onCancel={() => setPlanFor(null)}
                                onDeleted={() => {
                                  setPlanFor(null);
                                  refresh();
                                  toast("分割の予定を消しました。一括で請求できます");
                                }}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      ) : tab === "recurring" ? (
        <RecurringTab g={g} />
      ) : tab === "schedule" ? (
        <ScheduleTab g={g} available={!!meQ.data?.features.schedule} />
      ) : tab === "vendor" ? (
        // 見積依頼（#119、docs/56 §6.5。#198 から独立したタブ）：この{project}の見積の依頼。押すと見積依頼書の画面。使う機能 rfq の会社だけ（#149）
        <div role="tabpanel" id="gpanel-vendor" aria-labelledby="gtab-vendor" className="gpanel">
          <GroupRfqs groupId={g.id} showEmpty />
        </div>
      ) : tab === "dnotes" ? (
        // {deliveryNote}（#266、docs/96）：この{project}の{deliveryNote}。「{deliveryNote}作成」は見積なし（起こし方2）。見積からは受注した見積の画面
        <div role="tabpanel" id="gpanel-dnotes" aria-labelledby="gtab-dnotes" className="gpanel">
          <GroupDeliveryNotes
            groupId={g.id}
            side={
              <>
                <Link className="btn" to={`/delivery-notes/new?group=${g.id}`} data-testid="group-dn-new">
                  {t(DELIVERY_NOTE_MSG.create)}
                </Link>
                {/* 検収書（#266 1b）：使う機能「{acceptance}」の会社だけ（{project}から＝見積なし） */}
                {uses(meQ.data, "acceptance") ? (
                  <Link className="btn" to={`/delivery-notes/new?kind=acceptance&group=${g.id}`} data-testid="group-ac-new">
                    {t(ACCEPTANCE_MSG.create)}
                  </Link>
                ) : null}
              </>
            }
          />
        </div>
      ) : tab === "orders" ? (
        <div role="tabpanel" id="gpanel-orders" aria-labelledby="gtab-orders" className="gpanel">
          <GroupOrders groupId={g.id} />
        </div>
      ) : tab === "purchases" ? (
        <div role="tabpanel" id="gpanel-purchases" aria-labelledby="gtab-purchases" className="gpanel">
          <GroupPurchases groupId={g.id} />
          {/* 支払通知書（#233 3b）：この{project}の{paymentNotice}（状態チップ＝確認待ち n日／確認済 MM/DD／確認済（期限経過）／誤りあり／下書き） */}
          <GroupNotices groupId={g.id} />
        </div>
      ) : tab === "payments" ? (
        meQ.data ? <Slot name="group.payments" me={meQ.data} group={g} /> : null
      ) : tab === "deliveries" ? (
        <div role="tabpanel" id="gpanel-deliveries" aria-labelledby="gtab-deliveries" className="gpanel">
          {/* 右上に主ボタン「送付状作成」（記録が 0件でも出す。PC だけ＝スマホ版のタブは見るだけ。★14） */}
          {pcOnly.hidden("/cover-letters/new") ? null : (
            <div className="gpanel-acts">
              <Link className="btn primary" to={coverNewTo} data-testid="group-deliveries-create">
                {COVER_LETTER_MSG.create}
              </Link>
            </div>
          )}
          {dlv.isError ? <ErrorText>{messageOf(dlv.error)}</ErrorText> : dlv.data ? <DeliveryTable items={dlv.data.items} testId="group-deliveries" /> : <p className="hint">{COVER_LETTER_MSG.loading}</p>}
        </div>
      ) : (
        <div role="tabpanel" id="gpanel-invoices" aria-labelledby="gtab-invoices" className="gpanel">
          {invoices.length === 0 ? (
            <div className="gempty">
              <p style={{ margin: 0 }}>請求書はありません。<Help testId="help-no-invoice" text="受注した見積の「請求書作成」から作成します。" /></p>
            </div>
          ) : narrow ? (
            <ul className="gcards" data-testid="group-invoices-cards">
              {invoices.map(({ inv, job }) => (
                <li key={inv.id}>
                  <Link className="gcard" to={`/invoices/${inv.id}`} data-testid="group-invoice-card">
                    <span className="gc-no">{inv.documentNo ? <span className="num">{inv.documentNo}</span> : <span className="muted gc-nono">未発行</span>}</span>
                    <span className="gc-subj">
                      {job.subject || <span className="muted">（{L.subject}なし）</span>}
                      <span className="muted gc-sub">見積 {job.displayNo ?? "（下書き）"}</span>
                    </span>
                    <span className="gc-foot">
                      <span className="gc-st">{inv.status === "draft" ? <span className="chip">下書き</span> : (INVOICE_STATUS[inv.status] ?? "")}</span>
                      <span className="gc-amt"><span className="gc-lbl">税込</span> <span className="num">{yen(inv.total)} 円</span></span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="tbl-wrap">
              <table className="list" data-testid="group-invoices">
                <thead>
                  <tr>
                    <th>番号</th>
                    <th>どの見積から</th>
                    <th>いまの状態</th>
                    <th className="amt">金額（税込）</th>
                    <th>請求日</th>
                    <th>お支払期限</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(({ inv, job }) => (
                    <tr key={inv.id} className="r" tabIndex={0} onClick={() => nav(`/invoices/${inv.id}`)} onKeyDown={(e) => e.key === "Enter" && e.target === e.currentTarget && nav(`/invoices/${inv.id}`)} data-testid="group-invoice-row">
                      <td className="num">{inv.documentNo ?? <span className="muted">未発行</span>}</td>
                      <td>
                        {job.subject || <span className="muted">（{L.subject}なし）</span>}
                        <div className="muted" style={{ fontSize: 14 }}>見積 {job.displayNo ?? "（下書き）"}</div>
                      </td>
                      <td>{inv.status === "draft" ? <span className="chip">下書き</span> : (INVOICE_STATUS[inv.status] ?? "")}</td>
                      <td className="amt num"><AmtCell inc={inv.total} ex={inv.subtotal} testId="invoice-row-ex" /></td>
                      <td className="num">{md(inv.issueDate)}</td>
                      <td className="num">{inv.dueDate ? md(inv.dueDate) : <span className="muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {toastNode}
    </>
  );
}

/**
 * 見積の1行。請求書の列：
 *   ・分割の予定がある：次の回の「n回目の請求書をつくる（中間金）」（下書きがあればそれをひらく／すべて出したら「すべて請求済み」）
 *   ・分割の案件で、受注してまだ予定が無い：「分割予定作成」（脇に「一括請求」）
 *   ・一括：受注してまだ無ければ「請求書作成」、あればいちばん新しい請求書の番号（ほかは請求書のタブ）
 */
function GroupJobRow({
  j,
  chip,
  timing,
  onOpen,
  onInvoice,
  onInstallment,
  onPlanNew,
}: {
  j: GroupJob;
  chip: React.ReactNode;
  timing: BillingTiming;
  onOpen: () => void;
  onInvoice: () => void;
  onInstallment: (installmentId: string) => void;
  onPlanNew: () => void;
}) {
  const L = useLabels();
  const last = j.invoices.length ? j.invoices[j.invoices.length - 1]! : null;
  const plan = j.plan;
  return (
    <tr className="r" onClick={onOpen} tabIndex={0} onKeyDown={(e) => e.key === "Enter" && e.target === e.currentTarget && onOpen()}>
      <td className="num">
        {j.displayNo ?? <span className="muted">未発行</span>}
        {j.draftRevisionNo != null ? <span className="chip" title={`未発行の ${j.draftRevisionNo}回目があります`}>{j.draftRevisionNo}回目 未発行</span> : null}
      </td>
      <td>{j.subject || <span className="muted">（{L.subject}はまだ）</span>}</td>
      <td>
        {chip}
        {plan ? <PlanProgress plan={plan} /> : (j.status === "ordered" || j.status === "billed") && j.invoices.length ? <span className="muted" style={{ marginLeft: 8, fontSize: 14 }} data-testid="row-lump">一括</span> : null}
      </td>
      <td className="amt num">{j.total ? <AmtCell inc={j.total} ex={j.subtotal} testId="job-row-ex" /> : <span className="muted">—</span>}</td>
      <td className="num">{md(j.issueDate)}</td>
      <td onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        {plan ? (
          plan.next == null ? (
            <span className="muted">すべて請求済</span>
          ) : plan.next.draftInvoiceId ? (
            <span className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <Link className="small" to={`/invoices/${plan.next.draftInvoiceId}`} data-testid="plan-open-draft">{plan.next.seq}回目下書き表示</Link>
            </span>
          ) : (
            <button type="button" className="small ai" disabled={!!plan.error} onClick={() => onInstallment(plan.next!.id)} data-testid="plan-make-invoice">
              {plan.next.seq}回目請求書作成{plan.next.label ? `（${plan.next.label}）` : ""}
            </button>
          )
        ) : timing === "split" && j.canCreateInvoice ? (
          <span className="row" style={{ gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <button type="button" className="small ai" onClick={onPlanNew} data-testid="plan-new">分割予定作成</button>
            {/* 主役は「分割予定作成」。一括請求の道は「⋮」に（#92、components/RowMenu.tsx） */}
            <RowMenu label={`${j.subject || j.displayNo || "この見積"} のほかの動き`} testId="row-more" items={[{ label: "一括請求", onSelect: onInvoice, testId: "group-make-invoice-lump" }]} />
          </span>
        ) : j.canCreateInvoice ? (
          <button type="button" className="small ai" onClick={onInvoice} data-testid="group-make-invoice">請求書作成</button>
        ) : last && timing === "split" && j.invoices.length === 1 && last.status === "draft" ? (
          // 分割の案件なのに一括の下書きがある（予定を作る前に「請求書作成」を押した）。捨てれば分割にできる
          <span className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Link className="btn text num" to={`/invoices/${last.id}`}>一括下書き</Link>
            <span className="muted" style={{ fontSize: 14 }} data-testid="lump-draft-hint">分割にするなら、その下書きを捨ててから「分割予定作成」</span>
          </span>
        ) : last ? (
          <Link className="btn text num" to={`/invoices/${last.id}`}>
            {last.documentNo ?? "（下書き）"}
            {j.invoices.length > 1 ? <span className="muted">　ほか{j.invoices.length - 1}枚</span> : null}
          </Link>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
    </tr>
  );
}

/**
 * 社内メモ（2026-09-22 射場さん：案件内容と書類のタブのあいだに独立して置く）。
 *   #202（2026-10-17、docs/82 §3）：全文を自動保存で送るのをやめた（2人が同時に打つと、あとの全文が先の追記を消していた）。
 *   ・メモは読むだけの表示。下の「追記する内容」に打って「追記」（⌘／Ctrl＋Enter でも）→ POST /groups/:id/memo/append が
 *     末尾に「[日時 氏名] 本文」を足す（版は見ない＝2人が同時に追記しても両方残る）。送るのは「追記」を押したときだけ（打つたびには送らない＝
 *     1回ごとに日時と氏名が付くため。送っている間に打ち足した分も、もう一度押すまで送らない）。
 *     押さずに画面を離れるときは「続ける／破棄」を確かめる（共通の仕組み lib/saveQueue.tsx の離れるときの確かめ。勝手に追記しない）。
 *     ページを閉じるときはブラウザの確かめ
 *   ・全文の書き換えは見出しの右の「編集」だけ（版つきの PUT。ぶつかったら、ほかで末尾に足された分をこの窓の文の末尾に足して保存を待つ）
 *   ・9/22 の「日時と名前を追記＝先頭に入れる（新しいメモが上）」は改めた（追記は末尾）
 *   ・暗号化して保存（project_groups.memo_enc）。紙には出ない
 */
function MemoBox({ g, onSaved }: { g: GroupDetail; onSaved: (next: GroupDetail) => void }) {
  const { toast, node: toastNode } = useToast();
  const [draft, setDraft] = useState("");
  const latest = useRef(draft);
  latest.current = draft;
  const bodyRef = useRef<HTMLDivElement>(null);
  /** 「追記」を押した（送ってよい）。送り終えたら下ろす＝打ち足した分は、もう一度押すまで送らない */
  const armed = useRef(false);
  const sq = useSaveQueue({
    // 押すまでは送らない（書きかけの印だけ立てる＝離れるときに「続ける／破棄」を確かめる）
    blocked: () => !armed.current,
    // ページを閉じるときに keepalive で勝手に追記しない（閉じると消える書きかけはブラウザの確かめ）
    send: async () => {
      const sent = latest.current;
      const text = sent.trim();
      if (!text) {
        armed.current = false;
        return;
      }
      let next: GroupDetail;
      try {
        next = await api<GroupDetail>("POST", `/groups/${g.id}/memo/append`, { text });
      } catch (e) {
        // つながらない・混み合い（0・502〜504）は押したままにして、共通の仕組みが 5 秒後に送り直す。断られた（400 など）はもう一度押すまで送らない
        if (!(e instanceof ApiError && [0, 502, 503, 504].includes(e.status))) armed.current = false;
        throw e;
      }
      armed.current = false;
      // 送っている間に打ち足した分は残す（送った分だけ欄から消す）
      setDraft((d) => (d.startsWith(sent) ? d.slice(sent.length).replace(/^\s+/, "") : d));
      latest.current = latest.current.startsWith(sent) ? latest.current.slice(sent.length).replace(/^\s+/, "") : latest.current;
      onSaved(next);
      toast(MEMO_MSG.appended);
    },
  });
  const st = sq.state;
  // 新しい追記は末尾＝表示の枠はいちばん下を見せる
  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [g.memo]);
  const change = (v: string) => {
    setDraft(v);
    latest.current = v;
    if (v.trim()) sq.mark();
    else sq.discard();
  };
  const append = () => {
    if (!latest.current.trim()) return;
    armed.current = true;
    sq.mark(true);
  };
  /* ---- 全文の編集（版つき） ---- */
  const [editing, setEditing] = useState<{ base: string; version: string; text: string; busy: boolean; note?: string | undefined; error?: string | undefined } | null>(null);
  const openEdit = () => setEditing({ base: g.memo, version: g.updatedAt, text: g.memo, busy: false });
  const saveEdit = async () => {
    if (!editing) return;
    setEditing({ ...editing, busy: true, error: undefined });
    try {
      const next = await api<GroupDetail>("PUT", `/groups/${g.id}`, { memo: editing.text, version: editing.version } satisfies GroupUpdate);
      onSaved(next);
      setEditing(null);
      toast(MEMO_MSG.saved);
    } catch (e) {
      if (!(e instanceof ApiError && e.code === "GROUP_STALE")) {
        setEditing((x) => (x ? { ...x, busy: false, error: messageOf(e) } : x));
        return;
      }
      // ほかで変わっていた：最新を読み、ほかで末尾に足された分をこの窓の文の末尾に足す（足しただけでなければ、保存すると上書き）。保存はもう一度押してもらう
      const latestG = await api<GroupDetail>("GET", `/groups/${g.id}`);
      onSaved(latestG);
      setEditing((x) => {
        if (!x) return x;
        const merged = rebaseMemo(x.base, x.text, latestG.memo);
        return { base: latestG.memo, version: latestG.updatedAt, text: merged ?? x.text, busy: false, note: merged == null ? CONFLICT_MSG.memoOverwrite : CONFLICT_MSG.memoMerged };
      });
    }
  };
  const status =
    st.kind === "saving" && sq.isInflight() ? MEMO_MSG.appending : st.kind === "error" ? `⚠ 保存できませんでした：${st.msg}` : "";
  return (
    <section className="paper gmemo" data-testid="group-memo-box">
      <div className="row" style={{ marginBottom: 6, alignItems: "center" }}>
        <h2 style={{ margin: 0 }}>{MEMO_MSG.title}<Help testId="help-memo" text={MEMO_MSG.help} /></h2>
        <span className="hint" aria-live="polite" data-testid="group-memo-status" style={{ marginLeft: 12 }}>
          {status}
        </span>
        <span className="sp" />
        <button type="button" className="small" onClick={openEdit} data-testid="group-memo-edit">{MEMO_MSG.edit}</button>
      </div>
      {g.memo ? (
        <div ref={bodyRef} className="gmemo-body" data-testid="group-memo-text">
          {g.memo}
        </div>
      ) : (
        <p className="hint" data-testid="group-memo-text">{MEMO_MSG.empty}</p>
      )}
      <div className="gmemo-add">
        <textarea
          id="group_memo"
          className="inp memo"
          aria-label={MEMO_MSG.appendLabel}
          placeholder={MEMO_MSG.appendLabel}
          rows={Math.min(8, Math.max(2, draft.split("\n").length + 1))}
          maxLength={GROUP_MEMO_APPEND_MAX}
          value={draft}
          onChange={(e) => change(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && !e.nativeEvent.isComposing) {
              e.preventDefault();
              append();
            }
          }}
          data-testid="group-memo"
        />
        <button type="button" className="btn" disabled={!draft.trim()} onClick={append} data-testid="group-memo-append">{MEMO_MSG.append}</button>
      </div>
      {editing ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && !editing.busy && setEditing(null)}>
          <div className="dlg" role="dialog" aria-label={MEMO_MSG.editTitle} onKeyDown={(e) => e.key === "Escape" && !editing.busy && setEditing(null)} data-testid="group-memo-dialog">
            <div className="dh">{MEMO_MSG.editTitle}</div>
            <div className="db">
              <textarea
                className="inp memo"
                aria-label={MEMO_MSG.title}
                rows={12}
                autoFocus
                value={editing.text}
                onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                data-testid="group-memo-full"
              />
              {editing.note ? <p className="hint" role="status" data-testid="group-memo-note">{editing.note}</p> : null}
              <ErrorText>{editing.error}</ErrorText>
            </div>
            <div className="df">
              <button type="button" className="btn quiet" disabled={editing.busy} onClick={() => setEditing(null)}>{MEMO_MSG.cancel}</button>
              <button type="button" className="btn primary" disabled={editing.busy || editing.text === editing.base} onClick={() => void saveEdit()} data-testid="group-memo-save">{MEMO_MSG.save}</button>
            </div>
          </div>
        </div>
      ) : null}
      {toastNode}
      {sq.dialog}
    </section>
  );
}

/**
 * 継続案件の月別の表（#289、docs/101 §8、Figma ④ の詳細）。月・売上・原価・粗利・未請求（税抜）。
 * 数字は DB の v_project_monthly（売上の基準は会社の設定＝ホームの今月の売上と同じ式）。粗利は原価が1件も無い月は「—」（ルール7）
 */
function GroupMonthlyTable({ id }: { id: string }) {
  const q = useQuery({ queryKey: ["group-monthly", id], queryFn: () => api<GroupMonthly>("GET", `/groups/${id}/monthly`) });
  const rows = q.data?.items ?? [];
  return (
    <section className="paper gcontent" data-testid="group-monthly">
      <h2 style={{ margin: "0 0 6px" }}>{ONGOING_MSG.monthlyTitle}</h2>
      {q.isError ? <ErrorText>{messageOf(q.error)}</ErrorText> : null}
      <div className="tbl-wrap">
        <table className="list" aria-busy={q.isPending}>
          <thead>
            <tr>
              <th>{ONGOING_MSG.monthCol}</th>
              <th className="amt">{ONGOING_MSG.salesCol}</th>
              <th className="amt">{ONGOING_MSG.costCol}</th>
              <th className="amt">{ONGOING_MSG.marginCol}</th>
              <th className="amt">{ONGOING_MSG.unbilledCol}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.month} data-testid="group-monthly-row">
                <td className="num">{monthLabelOf(r.month)}</td>
                <td className="amt num">{yen(r.sales)} 円</td>
                <td className="amt num">{r.cost ? `${yen(r.cost)} 円` : <span className="muted">—</span>}</td>
                <td className="amt num">{r.margin == null ? <span className="muted">—</span> : `${yen(r.margin)} 円`}</td>
                <td className="amt num">{r.unbilled ? `${yen(r.unbilled)} 円` : <span className="muted">—</span>}</td>
              </tr>
            ))}
            {q.data && !rows.length ? (
              <tr className="emptyrows">
                <td colSpan={5}>{ONGOING_MSG.monthlyNone}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}
